/**
 * RE-VERIFICATION (#145)
 *
 * Where a rejected buyer lands after signing in. Deliberately outside
 * ProtectedRoutes: the backend caps this session to the user's own record,
 * so this page is the only thing it can reach. Mirrors the dashboard's
 * /auth/reverify and mobile's (auth)/reverify.tsx.
 */
import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { patchApi } from '../../api';
import { logout as logoutAction, update as updateSession } from '../../store/reducers/userSlice';
import Button from '../ui/Button';
import {
	isRejectedSession,
	userFromSession,
	verificationNote,
} from '../../utils/verification';

const schema = Yup.object().shape({
	// Mirrors the backend: optional, but exactly 9 digits when set.
	pan_number: Yup.string().test(
		'pan',
		'PAN number must be exactly 9 digits',
		(val) => !val || /^\d{9}$/.test(val)
	),
});

const readAsDataUrl = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
	});

const Reverify = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { user: session } = useSelector((state) => state.user);
	const user = userFromSession(session);

	const [businessRegFile, setBusinessRegFile] = useState(null);
	const [verificationDocFile, setVerificationDocFile] = useState(null);
	const [submitted, setSubmitted] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const rejected = isRejectedSession(session);
	const note = verificationNote(session);
	const initialPan = user?.pan_number || '';

	const handleSignOut = () => {
		dispatch(logoutAction(''));
		navigate('/login', { replace: true });
	};

	const handleSubmit = async (values) => {
		const panChanged = values.pan_number && values.pan_number !== initialPan;
		if (!panChanged && !businessRegFile && !verificationDocFile) {
			toast.error('Upload at least one new document, or correct your PAN.');
			return;
		}

		setIsLoading(true);
		try {
			const payload = {};
			if (panChanged) payload.pan_number = values.pan_number;
			if (businessRegFile) payload.business_registration = await readAsDataUrl(businessRegFile);
			if (verificationDocFile) payload.verification_document = await readAsDataUrl(verificationDocFile);

			// apiRequest resolves to { data, headers } — the updated user is in `data`.
			const { data: updated } = await patchApi(`system/users/${user?.uuid}/`, payload);
			// Fold the server's new state back into the stored session, or a
			// reload would show the rejected form again for an account that is
			// now pending.
			dispatch(
				updateSession({
					...session,
					user: { ...user, ...(updated || {}) },
				})
			);
			setSubmitted(true);
			toast.success('Documents submitted — your account is back under review.');
		} catch (error) {
			toast.error(
				error?.response?.data?.error || error?.message || 'Could not submit. Please try again.'
			);
		} finally {
			setIsLoading(false);
		}
	};

	// Once the re-submission lands the account is `pending`; nothing to do but wait.
	const underReview = submitted || !rejected;

	return (
		<div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
			<div className="sm:mx-auto sm:w-full sm:max-w-md">
				<div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
					<h2 className="text-2xl font-bold mb-4 text-center">
						{underReview ? 'Account Under Review' : 'Verification Rejected'}
					</h2>

					{underReview ? (
						<p className="text-gray-600 mb-6 text-center">
							Your documents are with our team. You&apos;ll get full access as soon as the
							account is approved.
						</p>
					) : (
						<>
							<p className="text-gray-600 mb-2 text-center">
								Our team could not verify your account. Upload clear replacement documents
								below and we&apos;ll review it again.
							</p>
							{note && (
								<p className="text-red-600 font-medium mb-4 text-center">Reason: {note}</p>
							)}

							<Formik
								initialValues={{ pan_number: initialPan }}
								validationSchema={schema}
								onSubmit={handleSubmit}
							>
								{({ errors, touched }) => (
									<Form>
										<div className="mb-4">
											<label
												htmlFor="pan_number"
												className="block text-gray-500 font-medium text-sm leading-none mb-2"
											>
												PAN Number
											</label>
											<Field
												id="pan_number"
												name="pan_number"
												placeholder="Your PAN Number"
												className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
											/>
											{errors.pan_number && touched.pan_number && (
												<div className="text-red-500 text-sm mt-1">{errors.pan_number}</div>
											)}
										</div>

										<div className="mb-4">
											<label
												htmlFor="business_registration"
												className="block text-gray-500 font-medium text-sm leading-none mb-2"
											>
												Business Registration
											</label>
											<input
												id="business_registration"
												type="file"
												accept="image/*"
												onChange={(e) => setBusinessRegFile(e.target.files[0])}
												className="w-full text-sm"
											/>
										</div>

										<div className="mb-6">
											<label
												htmlFor="verification_document"
												className="block text-gray-500 font-medium text-sm leading-none mb-2"
											>
												Verification Document
											</label>
											<input
												id="verification_document"
												type="file"
												accept="image/*"
												onChange={(e) => setVerificationDocFile(e.target.files[0])}
												className="w-full text-sm"
											/>
										</div>

										<Button type="submit"
											loading={isLoading}
											disabled={isLoading}
											className="w-full">
											Submit for review
										</Button>
									</Form>
								)}
							</Formik>
						</>
					)}

					<button
						type="button"
						onClick={handleSignOut}
						className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700"
					>
						Sign out
					</button>
				</div>
			</div>
		</div>
	);
};

export default Reverify;
